import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { ExportFileType, StaleTime, QueryTag } from '@common/ts/enums';
import { QUERY_KEYS } from '@common/react-query/query-keys';
import axiosInstance from '@/axios/axiosInstance';
import axiosInstanceAnalyze from '@/axios/AxiosInstanceAnalyze';
import axiosInstanceNoAuth from '@/axios/axiosInstanceNoAuth';
import { API_ENDPOINTS } from '@/api-endpoints';
import {
  AllTranscriptsProps,
  CategoryApiResponse,
  FiltersDataToSendProps,
  Insights,
  Organization,
  OverviewData,
  Project,
  ProjectProps,
  Transcripts,
  User,
  WorkspaceMemberSubset,
  ThemeSubset,
  Theme,
  OrganizationMemberSubset,
  PaginationConfig,
  PaginatedResult,
  PaginatedApiResponse,
  WorkspaceGroupsResponse,
  WorkspaceWithProjectCount,
  FiltersConfig,
  InterviewQuestion,
  InsightsSubset,
  AiSearchResults,
  AiSearchResultsSubset,
  ProjectMetadata,
  GetConversationsPageParams,
  ExportParams,
} from '@/types';
import { ECategoryMode, TranscriptsView } from '@/enums';
import { useMutateApi, useMutateApiWithStore } from './post';
import useCreateProjectStore from '@/store/CreateProjectStore';
import useUserStore from '@/store/UserStore';
import { useCurrentConversations } from '@/hooks/useCurrentConversations';

const fetchData = async <T>(
  endpoint: string,
  instance = axiosInstance,
  token = null as string | null,
): Promise<T> => {
  const { data } = await instance.get(
    endpoint,
    token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
  );
  return data;
};

export const useGetUser = (enabled = false) => {
  const { setUser, setIsLoading } = useUserStore();

  const {
    data: user,
    refetch,
    isPending,
  } = useQuery<User>({
    queryKey: QUERY_KEYS.USER,
    queryFn: () => fetchData<User>(API_ENDPOINTS.USERS.GET_USER_DATA),
    enabled,
  });

  useEffect(() => {
    if (user) setUser(user);
    if (enabled) setIsLoading(isPending);
  }, [user, setUser, isPending, setIsLoading, enabled]);

  return { data: user, refetch };
};

export const useGetUserWithToken = () =>
  useMutation({
    mutationFn: (token: string) =>
      fetchData<User>(API_ENDPOINTS.USERS.GET_USER_DATA, axiosInstanceNoAuth, token),
  });

export const useValidateInvitation = () =>
  useMutation({
    mutationFn: ({ organizationId, token }: { organizationId: string; token: string }) =>
      fetchData<{ valid: boolean }>(
        API_ENDPOINTS.ORGANIZATIONS.VALIDATE_INVITE(organizationId),
        axiosInstanceNoAuth,
        token,
      ),
  });

export const useGetTranslationLanguages = (projectId?: string, isTranscriptsTab?: boolean) =>
  useQuery({
    queryKey: QUERY_KEYS.PROJECT_LANGUAGES(projectId),
    queryFn: () =>
      fetchData<string[]>(API_ENDPOINTS.RESULTS.GET_LANGUAGES(projectId!), axiosInstanceAnalyze),
    enabled: !!projectId && isTranscriptsTab,
    staleTime: StaleTime.VERY_LONG,
  });

export const useGetOrganizationsOnLogin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      fetchData<PaginatedApiResponse<Organization>>(
        API_ENDPOINTS.ORGANIZATIONS.GET_ALL,
        axiosInstanceNoAuth,
        token,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS.ORGANIZATIONS, data);
    },
  });
};

export const useGetOrganizations = () =>
  useQuery<PaginatedApiResponse<Organization>, unknown, PaginatedResult<Organization>>({
    queryKey: QUERY_KEYS.ORGANIZATIONS,
    queryFn: () =>
      fetchData<PaginatedApiResponse<Organization>>(API_ENDPOINTS.ORGANIZATIONS.GET_ALL),
    staleTime: StaleTime.VERY_LONG,
    select: ({ data, pagination }) => ({ items: data, pagination }),
  });

export const useGetAllProjects = (
  organizationId?: string,
  workspaceId?: string,
  paginationConfig?: PaginationConfig,
  filtersConfig?: FiltersConfig,
) =>
  useQuery<PaginatedApiResponse<Project>, unknown, PaginatedResult<Project>>({
    queryKey: QUERY_KEYS.PROJECTS(workspaceId!, paginationConfig?.page, filtersConfig),
    enabled: !!organizationId && !!workspaceId,
    queryFn: () =>
      fetchData<PaginatedApiResponse<Project>>(
        API_ENDPOINTS.PROJECTS.GET_ALL(
          organizationId!,
          workspaceId!,
          paginationConfig,
          filtersConfig,
        ),
      ),
    staleTime: StaleTime.MEDIUM,
    select: ({ data, pagination }) => ({ items: data, pagination }),
  });

export const useGetWorkspaceMembers = (
  organizationId?: string,
  workspaceId?: string,
  enabled = true,
) =>
  useQuery<
    PaginatedApiResponse<WorkspaceMemberSubset>,
    unknown,
    PaginatedResult<WorkspaceMemberSubset>
  >({
    queryKey: QUERY_KEYS.WORKSPACE_MEMBERS(workspaceId),
    enabled: !!workspaceId && !!organizationId && enabled,
    queryFn: () =>
      fetchData<PaginatedApiResponse<WorkspaceMemberSubset>>(
        API_ENDPOINTS.WORKSPACES.GET_MEMBERS(organizationId!, workspaceId!),
      ),
    staleTime: StaleTime.LONG,
    select: ({ data, pagination }) => ({
      items: data.map(({ _id, email, name, role, email_verified }) => ({
        _id,
        email,
        name,
        role,
        email_verified,
      })),
      pagination,
    }),
  });

export const useGetWorkspaceGroups = (organizationId?: string) =>
  useQuery<PaginatedApiResponse<WorkspaceWithProjectCount>, unknown, WorkspaceGroupsResponse>({
    queryKey: QUERY_KEYS.WORKSPACE_GROUPS(organizationId),
    queryFn: () =>
      fetchData<PaginatedApiResponse<WorkspaceWithProjectCount>>(
        API_ENDPOINTS.WORKSPACES.GET_ALL(organizationId!),
      ),
    enabled: !!organizationId,
    staleTime: StaleTime.LONG,
    select: ({ data, pagination }) => ({ items: data, pagination }),
  });

export const useGetOrganizationMembers = (
  organizationId?: string,
  paginationConfig?: PaginationConfig,
  filtersConfig?: FiltersConfig,
) =>
  useQuery<
    PaginatedApiResponse<OrganizationMemberSubset>,
    unknown,
    PaginatedResult<OrganizationMemberSubset>
  >({
    queryKey: QUERY_KEYS.ORGANIZATION_MEMBERS(
      organizationId!,
      paginationConfig?.page,
      filtersConfig,
    ),
    enabled: !!organizationId,
    queryFn: () =>
      fetchData<PaginatedApiResponse<OrganizationMemberSubset>>(
        API_ENDPOINTS.ORGANIZATIONS.GET_MEMBERS(organizationId!, paginationConfig, filtersConfig),
      ),
    staleTime: StaleTime.LONG,
    select: ({ data, pagination }) => ({
      items: data.map(
        ({ _id, email, name, invite_accepted, email_verified, updatedAt, invitations, role }) => ({
          _id,
          email,
          name,
          invite_accepted,
          email_verified,
          updatedAt,
          invitations,
          role,
        }),
      ),
      pagination,
    }),
    placeholderData: (previousData) => previousData,
  });

export const useGetThemes = (
  organizationId?: string,
  paginationConfig?: PaginationConfig,
  filtersConfig?: FiltersConfig,
) =>
  useQuery<PaginatedApiResponse<ThemeSubset>, unknown, PaginatedResult<ThemeSubset>>({
    queryKey: QUERY_KEYS.THEMES(organizationId!, paginationConfig?.page, filtersConfig),
    enabled: !!organizationId,
    queryFn: () =>
      fetchData<PaginatedApiResponse<ThemeSubset>>(
        API_ENDPOINTS.THEMES.GET_ALL(organizationId!, paginationConfig, filtersConfig),
      ),
    staleTime: StaleTime.LONG,
    select: ({ data, pagination }) => ({
      items: data.map(({ _id, name, createdAt }) => ({
        _id,
        name,
        createdAt,
      })),
      pagination,
    }),
    placeholderData: (previousData) => previousData,
  });

export const useGetTheme = (organizationId?: string, themeId?: string | null) =>
  useQuery({
    queryKey: QUERY_KEYS.THEME(organizationId, themeId),
    enabled: !!organizationId && !!themeId,
    queryFn: () => fetchData<Theme>(API_ENDPOINTS.THEMES.GET_BY_ID(organizationId!, themeId!)),
    staleTime: StaleTime.LONG,
  });

export const useGetSingleProjectData = (
  organizationId: string,
  workspaceId: string,
  projectId: string,
  options?: { enabled?: boolean },
) => {
  const { isMediaReadyForUpload } = useCreateProjectStore();
  const defaultEnabled = !!projectId && !!workspaceId && !!organizationId && !isMediaReadyForUpload;
  const isQueryEnabled =
    options?.enabled !== undefined ? options.enabled && defaultEnabled : defaultEnabled;

  return useQuery<ProjectProps, unknown>({
    queryKey: [QueryTag.SINGLE_PROJECT_DATA, projectId],
    enabled: isQueryEnabled,
    queryFn: () =>
      fetchData<ProjectProps>(
        API_ENDPOINTS.PROJECTS.GET_BY_ID(organizationId, workspaceId, projectId),
      ),
    select: (data) => data,
    retry: 0,
  });
};

export const useGetOverviewData = (filterId: string | null, projectId?: string) =>
  useQuery({
    queryKey: QUERY_KEYS.INTERVIEW_OVERVIEW(projectId, filterId),
    queryFn: () =>
      fetchData<OverviewData>(
        API_ENDPOINTS.RESULTS.GET_OVERALL(projectId!, filterId),
        axiosInstanceAnalyze,
      ),
    refetchOnWindowFocus: true,
    staleTime: StaleTime.MEDIUM,
  });

export const useGetInterviewQuestions = (filterId: string | null, projectId?: string) =>
  useQuery({
    queryKey: QUERY_KEYS.INTERVIEW_QUESTIONS(projectId, filterId),
    queryFn: () =>
      fetchData<InterviewQuestion[]>(
        API_ENDPOINTS.RESULTS.GET_INTERVIEW_QUESTIONS(projectId!, filterId),
        axiosInstanceAnalyze,
      ),
    enabled: !!projectId,
    staleTime: StaleTime.LONG,
  });

export const useGetProjectQuestionInsights = (
  filterId: string | null,
  projectId?: string,
  questionId?: string,
) =>
  useQuery<Insights, unknown, InsightsSubset>({
    queryKey: QUERY_KEYS.INTERVIEW_QUESTION_INSIGHTS(projectId, questionId, filterId),
    enabled: !!questionId && !!projectId,
    queryFn: () =>
      fetchData<Insights>(
        API_ENDPOINTS.RESULTS.GET_PER_QUESTION_INSIGHTS(projectId!, questionId!, filterId),
        axiosInstanceAnalyze,
      ),
    staleTime: StaleTime.LONG,
    select: ({
      summary,
      patterns_and_trends,
      themes,
      divergent_views_and_outliers,
      opportunities,
      quotes,
    }) => {
      return {
        summary,
        patterns_and_trends,
        themes,
        divergent_views_and_outliers,
        opportunities,
        quotes,
      };
    },
  });

export const useGetInterviewQuestionTransripts = (
  filterId: string | null,
  categoryType: ECategoryMode | null,
  projectId?: string,
  questionId?: string,
) =>
  useQuery({
    queryKey: QUERY_KEYS.QUESTION_TRANSCRIPTS(filterId, categoryType, projectId, questionId),
    enabled: !!questionId && categoryType !== undefined,
    queryFn: () =>
      fetchData<Transcripts[]>(
        API_ENDPOINTS.RESULTS.GET_PER_QUESTION_TRANSCRIPTS(
          projectId!,
          questionId!,
          categoryType,
          filterId,
        ),
        axiosInstanceAnalyze,
      ),
    staleTime: StaleTime.MEDIUM,
  });

export const useGetAISearchResults = (projectId: string | undefined, filterId: string | null) =>
  useQuery<AiSearchResults, unknown, AiSearchResultsSubset>({
    queryKey: QUERY_KEYS.AI_SEARCH_RESULTS(projectId, filterId),
    queryFn: () =>
      fetchData<AiSearchResults>(
        API_ENDPOINTS.AI_SEARCH.GET(projectId!, filterId),
        axiosInstanceAnalyze,
      ),
    staleTime: StaleTime.MEDIUM,
    enabled: !!projectId,
    select: ({ searches, project_id, _id }) => ({
      searches,
      project_id,
      _id,
    }),
  });

export const useGetFilters = (projectId?: string) =>
  useQuery({
    queryKey: QUERY_KEYS.PROJECT_FILTERS(projectId),
    queryFn: () =>
      fetchData<FiltersDataToSendProps[]>(
        API_ENDPOINTS.FILTERS.GET_BY_ID(projectId!),
        axiosInstanceAnalyze,
      ),
    staleTime: StaleTime.LONG,
  });

export const useGetProjectMetadata = (projectId: string | undefined) =>
  useQuery({
    queryKey: QUERY_KEYS.PROJECT_METADATA(projectId),
    queryFn: () =>
      fetchData<ProjectMetadata[]>(
        API_ENDPOINTS.FILTERS.GET_METADATA(projectId!),
        axiosInstanceAnalyze,
      ),
    enabled: !!projectId,
    staleTime: StaleTime.LONG,
  });

export const useGetQuestionCategories = (
  filterId: string | null,
  projectId?: string,
  questionId?: string,
  enablePolling?: boolean,
) =>
  useQuery({
    queryKey: QUERY_KEYS.INTERVIEW_QUESTION_CATEGORIES(projectId, questionId, filterId),
    queryFn: () =>
      fetchData<CategoryApiResponse>(
        API_ENDPOINTS.CATEGORIES.GET_BY_QUESTION(projectId!, questionId!, filterId),
        axiosInstanceAnalyze,
      ),
    enabled: !!projectId && !!questionId,
    staleTime: enablePolling ? 0 : StaleTime.LONG,
    refetchInterval: enablePolling ? 10000 : false, // Poll every 10 seconds if enabled
    refetchOnWindowFocus: !!enablePolling,
  });

export const useGetConversation = (projectId?: string, conversationId?: string) =>
  useQuery({
    queryKey: QUERY_KEYS.CONVERSATION(projectId, conversationId),
    queryFn: () =>
      fetchData<AllTranscriptsProps>(
        API_ENDPOINTS.RESULTS.GET_CONVERSATION_BY_ID(projectId, conversationId),
        axiosInstanceAnalyze,
      ),
    enabled: !!conversationId && !!projectId,
    staleTime: StaleTime.SHORT,
  });

export const useMutateDownloadConversation = (projectId?: string) => {
  return useMutateApi(
    ({ conversationId, fileType }: { conversationId: string; fileType?: ExportFileType }) =>
      axiosInstanceAnalyze.get(
        API_ENDPOINTS.RESULTS.EXPORT_CONVERSATION(projectId, conversationId, fileType),
      ),
  );
};

export const useMutateDownloadConversationDetails = () => {
  return useMutateApi(({ projectId, filterId, fileType, transcriptsView }: ExportParams) =>
    axiosInstanceAnalyze.get(
      API_ENDPOINTS.RESULTS.EXPORT_CONVERSATION_DETAILS(
        projectId,
        filterId,
        fileType,
        transcriptsView,
      ),
    ),
  );
};

export const useMutateDownloadConversationOverview = () => {
  return useMutateApi(({ projectId, filterId, fileType, transcriptsView }: ExportParams) =>
    axiosInstanceAnalyze.get(
      API_ENDPOINTS.RESULTS.EXPORT_CONVERSATION_OVERVIEW(
        projectId,
        filterId,
        fileType,
        transcriptsView,
      ),
    ),
  );
};

export const useMutateDownloadFullAnswersTranscript = () => {
  return useMutateApi(({ projectId, filterId, fileType, transcriptsView }: ExportParams) =>
    axiosInstanceAnalyze.get(
      API_ENDPOINTS.RESULTS.EXPORT_FULL_ANSWERS_TRANSCRIPT(
        projectId,
        filterId,
        fileType,
        transcriptsView,
      ),
    ),
  );
};

export const useMutateDownloadAnalysisReport = () => {
  return useMutateApi(({ projectId, filterId }: { projectId: string; filterId?: string | null }) =>
    axiosInstanceAnalyze.get(API_ENDPOINTS.RESULTS.EXPORT_ANALYSIS(projectId, filterId)),
  );
};

export const useMutateDownloadConversationMedia = () => {
  return useMutation({
    mutationFn: async ({
      projectId,
      conversationId,
      filename,
    }: {
      projectId: string;
      conversationId: string;
      filename: string;
    }) => {
      const response = await axiosInstanceAnalyze.get(
        API_ENDPOINTS.RESULTS.EXPORT_CONVERSATION_MEDIA(projectId, conversationId),
        { responseType: 'blob' },
      );
      // Trigger browser download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useGetAllConversations = (
  projectId: string | undefined,
  filterId: string | null,
  shouldFetch: boolean,
  language: string | null,
  refetchInterval: number,
  conversationFilter?: TranscriptsView,
  paginationConfig?: PaginationConfig,
) =>
  useQuery<
    PaginatedApiResponse<AllTranscriptsProps>,
    unknown,
    PaginatedResult<AllTranscriptsProps>
  >({
    queryKey: [
      QueryTag.CONVERSATIONS,
      {
        projectId,
        filterId,
        language,
        conversationFilter,
        page: paginationConfig?.page,
      },
    ] as const,
    queryFn: () =>
      fetchData<PaginatedApiResponse<AllTranscriptsProps>>(
        API_ENDPOINTS.RESULTS.GET_CONVERSATIONS(
          projectId!,
          filterId,
          language,
          conversationFilter,
          paginationConfig,
        ),
        axiosInstanceAnalyze,
      ),
    select: ({ data, pagination }) => ({ items: data, pagination }),
    enabled: shouldFetch && !!projectId,
    staleTime: StaleTime.MEDIUM,
    refetchInterval,
  });

export const useMutateGetConversationsPageWithStore = (
  isCompletedTranscriptsView: boolean,
  urlCurrentPage: number,
  resetCompletedConversationsStore = false,
) => {
  const {
    completedConversations,
    incompleteConversations,
    actions: { setCompletedConversations, setIncompleteConversations },
  } = useCurrentConversations();

  return useMutateApiWithStore(
    async (params) => {
      if (!params) throw new Error('No parameters provided for fetching conversations page');
      const {
        projectId,
        filterId,
        language,
        conversationFilter,
        page = 1,
        limit = 20,
      } = params as GetConversationsPageParams;

      const paginationConfig = { page, limit };
      const response = await fetchData<PaginatedApiResponse<AllTranscriptsProps>>(
        API_ENDPOINTS.RESULTS.GET_CONVERSATIONS(
          projectId,
          filterId,
          language,
          conversationFilter,
          paginationConfig,
        ),
        axiosInstanceAnalyze,
      );
      return { items: response.data, pagination: response.pagination };
    },
    [QueryTag.CONVERSATIONS],
    (data) => {
      // Update store with new conversations data
      const { pagination, items } = data;
      const shouldPrepend = urlCurrentPage > +pagination.currentPage;

      if (resetCompletedConversationsStore && isCompletedTranscriptsView) {
        setCompletedConversations(items);
        return;
      }

      if (isCompletedTranscriptsView) {
        const updatedConversations = shouldPrepend
          ? [...items, ...completedConversations]
          : [...completedConversations, ...items];
        setCompletedConversations(updatedConversations);
      } else {
        const updatedConversations = shouldPrepend
          ? [...items, ...incompleteConversations]
          : [...incompleteConversations, ...items];
        setIncompleteConversations(updatedConversations);
      }
    },
  );
};

// ==================== UNPUBLISH PROJECT FEATURE ====================

export interface ConversationCounts {
  active: number;
  completed: number;
  total: number;
}

export const useGetConversationCounts = (
  organizationId?: string,
  workspaceId?: string,
  projectId?: string,
  enabled = true,
) =>
  useQuery<ConversationCounts>({
    queryKey: ['conversationCounts', projectId],
    queryFn: () =>
      fetchData<ConversationCounts>(
        API_ENDPOINTS.PROJECTS.GET_CONVERSATION_COUNTS(organizationId, workspaceId, projectId),
      ),
    enabled: enabled && !!organizationId && !!workspaceId && !!projectId,
    staleTime: StaleTime.SHORT,
  });
